/**
 *  Simple class for check client session status with zero dependencies.
 */
export type SessionStatus = 'changed' | 'unchanged' | 'error'

export type EventData = {
  isActive: boolean,
  status: SessionStatus
}

export class Session {
  public readonly id = 'check-session-iframe'
  protected origin: string
  protected client: string
  protected session: string
  protected url: string

  public timeout: number

  #iid?: unknown
  #isMounted = false

  get iframe() {
    return document.getElementById(this.id) as HTMLIFrameElement | null
  }

  get message(): string {
    return `${this.client} ${this.session}`
  }

  constructor(client: string, session: string, url: string, timeout = 0, origin = '') {
    this.client = client
    this.session = session
    this.url = url
    this.timeout = timeout
    this.origin = origin || new URL(this.url || location.href).origin

    this.check = this.check.bind(this)
  }

  #onStatusChange(event: MessageEvent) {
    if (this.url.startsWith(event.origin) && this.iframe && ['changed', 'unchanged', 'error'].includes(event.data)) {
      const detail = { isActive: event.data === 'unchanged', status: event.data }
      this.iframe.dispatchEvent(new CustomEvent<EventData>('session:status', { detail }))
    }
  }

  mount() {
    return new Promise((resolve, reject) => {
      if (this.#isMounted) return resolve(undefined)

      const iframe = document.createElement('iframe')
      const onStatusChange = this.#onStatusChange.bind(this)

      window.addEventListener('message', onStatusChange)
      if (this.timeout > 0) this.#iid = setInterval(this.check, this.timeout)

      iframe.setAttribute('hidden', '')
      iframe.setAttribute('id', this.id)
      iframe.addEventListener('load', resolve)
      iframe.addEventListener('error', reject)

      iframe.src = this.url

      if (document.body.appendChild(iframe)) this.#isMounted = true
    })
  }

  check() {
    if (this.#isMounted && this.message.trim()) {
      this.iframe?.contentWindow?.postMessage(this.message, this.origin)
    }
  }

  unmount() {
    this.#iid && clearTimeout(this.#iid as number)
    window.removeEventListener('message', this.#onStatusChange)
    this.iframe && document.removeChild(this.iframe)
    this.#isMounted = false
  }
}
